import { Entity } from '@api/api/entity-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useCompanyCountryExposure } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '@terminal/routes/query-params';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GeoExposureDetails,
  loadingSkeletonAreas,
} from '../components/geo-exposure-details/GeoExposureDetails';

export interface CountryExposureDrawerContentProps {
  entity: Entity;
}

export const CountryExposureDrawerContent = ({
  entity,
}: CountryExposureDrawerContentProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();
  const countryCode = params[queryKeys.countryCode];

  const { data, isFetching, isError, error } = useCompanyCountryExposure({
    entityTag: entity.tag,
  });

  const selected = useMemo(
    () => data?.find(item => item.code === countryCode),
    [data, countryCode]
  );

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={loadingSkeletonAreas}
        data-testid="loading-skeleton"
      />
    );
  }

  if (isError || !selected || !countryCode) {
    throw new Error(error?.message || 'Country data not found');
  }

  return (
    <GeoExposureDetails
      entity={entity}
      targetName={countryCode}
      targetType="country"
      title={t(
        'widget:assetOverview.knowledgeGraph.geographicExposure.drawer.companyCountryExposure',
        {
          entity: entity.name_short || entity.name,
        }
      )}
      rankTitle={t(
        'widget:assetOverview.knowledgeGraph.geographicExposure.drawer.countryExposureRank'
      )}
      description={selected.relationship.description}
      rank={selected.relationship.rank}
      evidence={selected.relationship.evidence}
      sourceEvidence={selected.relationship.source_evidence}
    />
  );
};
